import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle, Phone, Mail, MapPin, Clock, Instagram, Facebook } from "lucide-react";
import { CONTACTS, NAV_LINKS, WA_URL, SIGNATURE } from "@/lib/site";
import { lenisRef } from "@/lib/lenis";

export function SynapseMark({ className = "w-11 h-8", indigo = "#4A3E96", orange = "#E9552B" }) {
  return (
    <svg viewBox="0 0 44 30" className={className} aria-hidden="true">
      <circle cx="16" cy="15" r="12.5" fill="none" stroke={indigo} strokeWidth="2.6" />
      <circle cx="28" cy="15" r="12.5" fill="none" stroke={orange} strokeWidth="2.6" />
    </svg>
  );
}

export function Marquee({ items, light = false }) {
  const row = items.map((t, i) => (
    <span key={i} className="flex items-center shrink-0">
      <span
        className={`font-sora font-semibold uppercase tracking-tight text-4xl md:text-6xl px-6 ${
          light ? "text-crema/15" : "text-indaco/10"
        }`}
      >
        {t.split("★").map((part, j, arr) => (
          <span key={j}>
            {part}
            {j < arr.length - 1 && <span className="text-arancio">★</span>}
          </span>
        ))}
      </span>
      <SynapseMark className={`w-10 h-7 shrink-0 ${light ? "opacity-25" : "opacity-20"}`} />
    </span>
  ));
  return (
    <div className="overflow-hidden py-10 select-none" aria-hidden="true">
      <div className="marquee-track">
        {row}
        {row.map((el, i) => ({ ...el, key: `dup-${i}` }))}
      </div>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-crema border-b border-ink/10" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <img src="/logo-synapses.png" alt="Logo Synapses" className="h-9 w-auto transition-transform duration-500 group-hover:scale-110" />
            <span className="font-sora font-bold text-lg tracking-tight text-ink">
              SYNAPSES
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="main-nav">
            {NAV_LINKS.filter((l) => l.to !== "/prenota").map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `nav-link text-sm font-medium text-ink/70 hover:text-ink transition-colors duration-300 ${
                    isActive ? "active" : ""
                  }`
                }
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-ink/70 hover:text-indaco transition-colors duration-300 flex items-center gap-2"
              data-testid="nav-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <Link
              to="/prenota"
              className="ml-2 inline-flex items-center rounded-full bg-indaco px-6 py-2.5 text-sm font-semibold text-crema transition-colors duration-300 hover:bg-indaco-deep"
              data-testid="nav-prenota-btn"
            >
              Prenota
            </Link>
          </nav>

          <button
            className="lg:hidden p-2 text-ink"
            onClick={() => setOpen(true)}
            aria-label="Apri menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-indaco-deep flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-crema/10">
              <div className="flex items-center gap-3">
                <img src="/logo-synapses.png" alt="Logo Synapses" className="h-9 w-auto" />
                <span className="font-sora font-bold text-lg tracking-tight text-crema">SYNAPSES</span>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 text-crema"
                aria-label="Chiudi menu"
                data-testid="mobile-menu-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    className="block font-sora font-bold text-4xl text-crema py-3 hover:text-arancio transition-colors duration-300"
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="mt-6 inline-flex items-center gap-3 text-crema/70"
                data-testid="mobile-nav-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                Scrivici su WhatsApp
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const FOOTER_ORARI = [
  { giorno: "Lunedì", ore: "14:00–18:30" },
  { giorno: "Martedì", ore: "14:00–18:30" },
  { giorno: "Mercoledì", ore: "9:00–13:00" },
  { giorno: "Giovedì", ore: "9:00–13:00" },
  { giorno: "Venerdì", ore: "14:00–18:30" },
  { giorno: "Sabato e domenica", ore: "chiuso" },
];

const FOOTER_ORARI_SHORT = [
  { giorno: "Lun, Mar, Ven", ore: "14:00–18:30" },
  { giorno: "Mer, Gio", ore: "9:00–13:00" },
  { giorno: "Sab, Dom", ore: "chiuso" },
];

function Footer() {
  const { pathname } = useLocation();
  const dark = pathname === "/team";
  const t = {
    heading: dark ? "text-crema/60" : "text-viole",
    body: dark ? "text-crema/90" : "text-ink/85",
    sub: dark ? "text-crema/60" : "text-viole",
    social: dark
      ? "border-crema/30 text-crema hover:bg-crema hover:text-indaco-deep hover:border-crema"
      : "border-ink/20 text-indaco hover:bg-indaco hover:text-crema hover:border-indaco",
    cookie: dark ? "text-crema/50" : "text-viole/80",
  };
  return (
    <footer
      className={`border-t ${dark ? "bg-indaco-deep text-crema border-crema/15" : "bg-crema text-ink border-ink/10"}`}
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 lg:py-10 grid gap-5 lg:gap-8 grid-cols-2 lg:grid-cols-12">
        <div className="col-span-2 lg:col-span-3">
          <div className="flex items-center gap-3 mb-3">
            <img src="/logo-synapses.png" alt="Logo Synapses" className="h-8 w-auto" />
            <span className="font-sora font-bold text-lg tracking-tight">SYNAPSES</span>
          </div>
          <p className={`${t.body} text-sm leading-relaxed max-w-xs`}>
            Trasformiamo sintomi confusi in risposte chiare.
          </p>
          <p className={`${t.sub} text-sm mt-2 leading-relaxed hidden sm:block`}>{SIGNATURE}</p>
        </div>
        <div className="col-span-2 lg:col-span-3">
          <p className={`text-sm uppercase tracking-[0.22em] font-semibold ${t.heading} mb-3`}>Contatti</p>
          <ul className="grid grid-cols-2 lg:block gap-x-4 gap-y-2 lg:space-y-2 items-end text-sm">
            <li>
              <a href={CONTACTS.telefonoHref} className={`flex items-center gap-3 ${t.body} hover:text-arancio transition-colors`} data-testid="footer-phone">
                <Phone className="w-4 h-4 shrink-0 text-arancio" /> {CONTACTS.telefono}
              </a>
              <div className="mt-3 flex gap-3 lg:hidden">
                <a
                  href="https://www.instagram.com/synapses.official/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram di Synapses"
                  className={`flex items-center justify-center w-9 h-9 rounded-full border transition-colors duration-300 ${t.social}`}
                  data-testid="footer-instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61584357297211&locale=it_IT"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook di Synapses"
                  className={`flex items-center justify-center w-9 h-9 rounded-full border transition-colors duration-300 ${t.social}`}
                  data-testid="footer-facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </li>
            <li className="hidden lg:block">
              <a href={WA_URL} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-3 ${t.body} hover:text-arancio transition-colors`} data-testid="footer-whatsapp">
                <MessageCircle className="w-4 h-4 shrink-0 text-arancio" /> WhatsApp: {CONTACTS.whatsappDisplay}
              </a>
            </li>
            <li className="hidden lg:block">
              <a href={`mailto:${CONTACTS.email}`} className={`flex items-center gap-3 ${t.body} hover:text-arancio transition-colors break-all`} data-testid="footer-email">
                <Mail className="w-4 h-4 shrink-0 text-arancio" /> {CONTACTS.email}
              </a>
            </li>
            <li>
              <a href={CONTACTS.mapsUrl} target="_blank" rel="noopener noreferrer" className={`flex items-start gap-3 ${t.body} hover:text-arancio transition-colors`} data-testid="footer-address">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-arancio" />
                <span>
                  {CONTACTS.indirizzo}
                  <span className={`block text-sm ${t.sub} mt-0.5`}>A 10 min a piedi da Porta Susa</span>
                </span>
              </a>
            </li>
          </ul>
        </div>
        <div className="hidden lg:block lg:col-span-2">
          <p className={`text-sm uppercase tracking-[0.22em] font-semibold ${t.heading} mb-3`}>Orari</p>
          <ul className={`space-y-1.5 text-sm lg:hidden ${t.body}`}>
            {FOOTER_ORARI_SHORT.map((o) => (
              <li key={o.giorno} className="flex flex-col">
                <span>{o.giorno}</span>
                <span className="font-medium">{o.ore}</span>
              </li>
            ))}
          </ul>
          <ul className={`hidden lg:block space-y-1 text-sm ${t.body}`}>
            {FOOTER_ORARI.map((o) => (
              <li key={o.giorno} className="flex justify-between gap-3">
                <span>{o.giorno}</span>
                <span className="font-medium">{o.ore}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="hidden lg:block lg:col-span-2">
          <div className="flex items-center gap-4 lg:block">
            <p className={`text-sm uppercase tracking-[0.22em] font-semibold ${t.heading} lg:mb-3`}>Social</p>
            <div className="flex gap-3">
              <a
                href="https://www.instagram.com/synapses.official/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram di Synapses"
                className={`flex items-center justify-center w-9 h-9 rounded-full border transition-colors duration-300 ${t.social}`}
                data-testid="footer-instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61584357297211&locale=it_IT"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook di Synapses"
                className={`flex items-center justify-center w-9 h-9 rounded-full border transition-colors duration-300 ${t.social}`}
                data-testid="footer-facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
        <div className="hidden lg:block col-span-2 lg:col-span-2">
          <p className={`text-sm uppercase tracking-[0.22em] font-semibold ${t.heading} mb-3`}>Navigazione</p>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className={`${t.body} hover:text-arancio transition-colors`} data-testid={`footer-nav-${l.label.toLowerCase()}`}>
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/privacy" className={`${t.body} hover:text-arancio transition-colors`} data-testid="footer-nav-privacy">
                Informativa privacy
              </Link>
            </li>
            <li>
              <Link to="/cookie-policy" className={`${t.body} hover:text-arancio transition-colors`} data-testid="footer-nav-cookie">
                Cookie policy
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className={`border-t ${dark ? "border-crema/15" : "border-ink/10"}`}>
        <div className={`max-w-7xl mx-auto px-6 lg:px-10 py-2.5 text-sm leading-snug ${t.sub} lg:hidden`} data-testid="footer-legal">
          Synapses S.r.l. STP · Sede legale: Corso F. Brunelleschi 173/C, 10141 Torino · P.IVA 12454950010 · REA TO-1291453 · Direttore Sanitario: Dott. Enrico Cavallo, Neurologo
        </div>
        <div className={`max-w-7xl mx-auto px-6 lg:px-10 py-2.5 text-sm leading-snug ${t.sub} hidden lg:block`} data-testid="footer-legal-desktop">
          Synapses S.r.l. Società tra Professionisti · Sede legale: Corso Filippo Brunelleschi 173/C, 10141 Torino (TO) · Sede operativa: Via Avigliana, 38, 10138 Torino (TO) · P.IVA e C.F. 12454950010 · Registro Imprese di Torino, REA TO-1291453 · Direttore Sanitario: Dott. Enrico Cavallo, specialista in Neurologia · Titolare del trattamento dei dati: Synapses S.r.l. STP
        </div>
      </div>
    </footer>
  );
}

function WhatsAppFab() {
  return (
    <a
      href={WA_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Scrivici su WhatsApp"
      className="fixed bottom-6 right-6 z-40 group flex items-center gap-3"
      data-testid="whatsapp-fab"
    >
      <span className="hidden md:block bg-ink text-crema text-sm font-medium px-4 py-2 rounded-full opacity-0 translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0">
        Una domanda? Ti rispondiamo entro 24 ore
      </span>
      <span className="flex items-center justify-center w-14 h-14 rounded-full bg-indaco text-crema shadow-xl shadow-indaco/30 transition-transform duration-300 group-hover:scale-110">
        <MessageCircle className="w-6 h-6" />
      </span>
    </a>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <div className="grain-overlay" aria-hidden="true" />
      <Header />
      <ScrollToTop />
      <main className="flex-1">{children}</main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
